import React, { createRef, useRef, useEffect, useCallback } from 'react';
import { useMediaQuery, PeekPopper, PeekClickAwayListener, PeekSlide, PeekPaper, PeekBox, PeekIconButton, PeekTypography, PeekStack } from '@piiqtechnologies/ui-components';
import { X } from '@piiqtechnologies/svg-icons';
import { usePannellumViewer } from '../pannellum-viewer/PannellumProvider.js';

const ViewerPopperActionControl = ({ actionId, children, ActionButton, actionDetailsPlacement = "start", autoDisplayNavigationCount, actionDetailsPositioningStrategy = "fixed", isEditorMode, actionDetailsSxProps, actionDetailsCloseSxProps, disableBackDropClick, autoDisplayTime, }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const anchorElRef = createRef();
    const open = Boolean(anchorEl);
    const navigationCount = useRef(0);
    const autoDisplay = useRef(false);
    const { sceneId } = usePannellumViewer() || {};
    const mobileView = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const handleViewerButtonClick = () => {
        setAnchorEl((anchorEl) => {
            if (!anchorEl)
                return anchorElRef.current;
            return null;
        });
    };
    useEffect(() => {
        if (autoDisplayNavigationCount && sceneId) {
            navigationCount.current = navigationCount.current + 1;
            if (navigationCount.current === autoDisplayNavigationCount) {
                setAnchorEl(anchorElRef.current);
            }
            else {
                setAnchorEl(null);
            }
        }
    }, [sceneId, autoDisplayNavigationCount]);
    useEffect(() => {
        if (autoDisplayTime) {
            const timer = setTimeout(() => {
                if (autoDisplay.current)
                    return;
                autoDisplay.current = true;
                setAnchorEl(anchorElRef.current);
            }, autoDisplayTime);
            return () => clearTimeout(timer);
        }
    }, [autoDisplayTime, anchorElRef]);
    const handleActionDetailsClose = useCallback(() => {
        setAnchorEl(null);
    }, []);
    const viewerActionButtonId = `${actionId}-button`;
    const viewerActionDetailsPopperId = `${actionId}-details`;
    const isEditorScreen = isEditorMode && !document.fullscreenElement;
    return (React.createElement(React.Fragment, null,
        React.createElement(ActionButton, { ref: anchorElRef, onActionClick: handleViewerButtonClick, id: viewerActionButtonId, "aria-controls": open ? viewerActionDetailsPopperId : undefined, "aria-haspopup": "true", "aria-expanded": open ? "true" : undefined, "aria-label": `show ${actionId} details`, selected: open }),
        !!children && (
        /* @ts-ignore */
        React.createElement(PeekPopper, { id: viewerActionDetailsPopperId, "aria-labelledby": viewerActionButtonId, anchorEl: anchorEl, open: open, role: undefined, placement: (!isEditorMode && ((mobileView && "left-end") || `top-${actionDetailsPlacement}`)) || "top", sx: Object.assign(Object.assign({ zIndex: 1 }, ((!isEditorMode && {
                transform: {
                    xs: "translate3d(0px, 0px, 0px) !important",
                    md: `translate3d(${(actionDetailsPlacement === "start" && 45) || -45}px, -130px, 0px) !important`,
                },
            }) || {
                transform: "translate3d(20px, -90px, 0px) !important",
            })), { width: { xs: (!isEditorMode && "100vw") || "auto", md: "auto" }, bottom: "0px !important", left: ((mobileView || actionDetailsPlacement === "start") && "0px !important") || "unset !important", right: (!mobileView && actionDetailsPlacement !== "start" && "0px !important") || "unset !important", top: "unset !important" }), disablePortal: true, popperOptions: { strategy: actionDetailsPositioningStrategy }, transition: true }, ({ TransitionProps }) => (React.createElement(PeekClickAwayListener, { onClickAway: () => !disableBackDropClick && handleActionDetailsClose() },
            React.createElement(PeekSlide, Object.assign({}, TransitionProps, { direction: (mobileView && "up") || (actionDetailsPlacement === "start" && "right") || "left", appear: true, exit: false }),
                React.createElement(PeekPaper, { sx: Object.assign({ p: 3, borderRadius: 2.5, boxShadow: "1px 1px 7px rgba(0, 0, 0, 0.14)" }, (actionDetailsSxProps || {})) },
                    React.createElement(PeekBox, { position: "absolute", sx: { top: 20, right: 20 } },
                        React.createElement(PeekIconButton, { "aria-label": `close ${actionId} details`, bordered: false, onClick: handleActionDetailsClose },
                            React.createElement(PeekTypography, { color: "common.grey", sx: actionDetailsCloseSxProps, lineHeight: 0 },
                                React.createElement(X, { height: 22, width: 22 })))),
                    React.createElement(PeekStack, { maxHeight: { xs: "70vh", md: isEditorScreen ? "240px" : "60vh" }, minWidth: (!isEditorMode && { md: 330 }) || 330, overflow: isEditorMode ? "auto" : "hidden" }, (typeof children === "function" && children({ onActionDetailsClose: handleActionDetailsClose })) ||
                        children)))))))));
};

export { ViewerPopperActionControl, ViewerPopperActionControl as default };
